import React, { useEffect, useState } from "react";
import { IconArrowNarrowRight } from "@tabler/icons-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMembers, getMemberInfo } from "@/hooks/useMembers";
import { isChild } from "@/types/member";
import { exitFamilyAsOwner } from "@/services/members.service";
import { familyService } from "@/services/family.service";
import FormHeader from "@/components/ui/organisms/MHeader";
import DeleteAccountDialog from "@/components/ui/organisms/DeleteAccountDialog";
import { MButton } from "@/components/ui/atoms/MButtons";
import { MInputField } from "@/components/ui/atoms/MInputField";
import { otpService } from "@/services/otp.service";
import NominateFamilyOwner from "./NominateFamilyOwner";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

const DeleteProfile: React.FC = () => {
  // Helper to extract error message from nested API error response
  const getErrorMessage = (
    err: any,
    fallback: string = "An error occurred"
  ) => {
    const apiError = err?.response?.data;
    if (typeof apiError?.message === "string") return apiError.message;
    if (typeof apiError?.message?.message === "string")
      return apiError.message.message;
    if (typeof err?.message === "string") return err.message;
    return fallback;
  };

  // ...existing hooks and state
  const [otpValue, setOtpValue] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [nominateStage, setNominateStage] = useState<
    "select" | "typed" | "otp" | "done"
  >("select");

  // Countdown for resendTimer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { deleteMember, exitFamilyAsMember } = useMembers();

  // Robustly extract state values with fallbacks
  const targetMember = location.state?.targetMember || getMemberInfo();
  const action = location.state?.action || "delete";

  // Use action for mode logic
  const mode = action === "remove" ? "remove" : "delete";

  // Get current user info (FO/FM)
  const loggedInMember = getMemberInfo(true); // logged-in user
  const isFamilyOwner = loggedInMember.family_member_type === "fo";
  const currentFamilyId = loggedInMember.current_family;

  // Fetch all family members for this family (using /api/family/members/getAll)
  const [familyMembers, setFamilyMembers] = useState<any[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  useEffect(() => {
    const fetchMembers = async () => {
      setMembersLoading(true);
      try {
        const res = await familyService.getFamilyMembers({
          filters: { family_id: currentFamilyId },
        });
        setFamilyMembers(res.data || []);
      } catch (err) {
        setFamilyMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };
    if (currentFamilyId) fetchMembers();
  }, [currentFamilyId]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [showOtp, setShowOtp] = useState(false);

  // Type DELETE confirmation state for pet profiles
  const [deleteInputValue, setDeleteInputValue] = useState("");
  const [deleteInputLoading, setDeleteInputLoading] = useState(false);
  const [showDeleteInput, setShowDeleteInput] = useState(false);

  // --- FO nomination state ---
  // Use registeredMembers from navigation state directly (already filtered)
  const eligibleForOwnership = location.state?.eligibleForOwnership || [];

  const [selectedNewFO, setSelectedNewFO] = useState<string>("");
  const [showNominateFO, setShowNominateFO] = useState(false);
  const [nominateLoading, setNominateLoading] = useState(false);
  const [nominateError, setNominateError] = useState("");
  // For FO exit/nomination flow
  const [pendingNominee, setPendingNominee] = useState<string | null>(null);

  useEffect(() => {
    if (!loggedInMember?.id || mode !== "delete") return;

    trackUserDataLoaded({
      memberInfo: loggedInMember,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_INFO_MEMBER_DELETE,
    });
  }, [loggedInMember?.id, mode]);

  useEffect(() => {
    if (showOtp && loggedInMember?.id && mode === "delete") {
      trackUserDataLoaded({
        memberInfo: loggedInMember,
        screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
        screenName: ANALYTICS_SCREEN_NAMES.FAMILY_INFO_MEMBER_DELETE_CONFIRM,
      });
    }
  }, [showOtp, loggedInMember?.id, mode]);

  /**
   * Unified confirm handler for Remove from Family and Delete Profile
   * - Remove from Family: requires typed first name confirmation, calls exitFamilyAsMember with action 'exit'
   * - Delete Profile: shows OTP flow and calls deleteMember after OTP
   */
  const handleConfirm = async () => {
    // If FO is deleting/exiting self
    if (isFamilyOwner && loggedInMember.id === targetMember.id) {
      if (eligibleForOwnership.length === 0) {
        // No eligible members, go straight to OTP
        // Setup OTP state for logged-in member
        let field: "email" | "mobile";
        let value = "";
        let dial = "";
        if (loggedInMember?.email) {
          field = "email";
          value = loggedInMember.email;
        } else if (loggedInMember?.mobile) {
          field = "mobile";
          value = loggedInMember.mobile;
          dial = loggedInMember.dialCode || "";
        } else {
          showToast({
            message: "No valid contact method found.",
            variant: "error",
          });
          return;
        }
        setOtpField(field);
        setOtpTargetValue(value);
        setDialCode(dial);
        setPendingNominee(null); // No nominee
        setOtpLoading(true);
        try {
          await otpService.sendOtp({
            send_to: value,
            dialCode: field === "mobile" ? dial : undefined,
            mode: field === "mobile" ? "sms" : "email",
            type: "member_delete",
          });
          setResendTimer(30);
          setShowOtp(true);
        } catch (err: any) {
          showToast({
            message: getErrorMessage(err, "Failed to send OTP."),
            variant: "error",
          });
        } finally {
          setOtpLoading(false);
        }
      } else {
        // There are eligible members, show nomination UI
        setShowNominateFO(true);
      }
      return;
    }

    // PET PROFILE or CHILD: show Type DELETE UI
    if (targetMember?.memberType === "p" || isChild(targetMember)) {
      setShowDeleteInput(true);
      return;
    }

    // REMOVE FROM FAMILY: Only for registered family targetMembers (not owner, not pet)
    if (
      (action === "remove" && targetMember.family_member_type === "fm") ||
      (action === "exit" &&
        targetMember.family_member_type === "fm" &&
        loggedInMember.id === targetMember.id)
    ) {
      // Show typed name confirmation UI for both FO removing FM and FM exiting self
      setShowRemoveInput(true);
      return;
    }

    // DELETE PROFILE: owner or self, proceed with OTP flow
    if (mode === "delete") {
      // Determine who should receive the OTP
      const isSelfDelete = targetMember.id === loggedInMember.id;
      const otpTarget = isSelfDelete ? targetMember : loggedInMember;
      let field: "email" | "mobile";
      let value = "";
      let dial = "";
      if (otpTarget?.email) {
        field = "email";
        value = otpTarget.email;
      } else if (otpTarget?.mobile) {
        field = "mobile";
        value = otpTarget.mobile;
        dial = otpTarget.dialCode || "";
      } else {
        showToast({
          message: "No valid contact method found.",
          variant: "error",
        });
        return;
      }
      setOtpField(field);
      setOtpTargetValue(value);
      setDialCode(dial);
      setOtpLoading(true);
      try {
        await otpService.sendOtp({
          send_to: value,
          dialCode: field === "mobile" ? dial : undefined,
          mode: field === "mobile" ? "sms" : "email",
          type: "member_delete",
        });
        setResendTimer(30);
        setShowOtp(true);
      } catch (err: any) {
        showToast({
          message: getErrorMessage(err, "Failed to send OTP."),
          variant: "error",
        });
      } finally {
        setOtpLoading(false);
      }
    }
  };

  // --- REMOVE FROM FAMILY: Typed name confirmation and API call ---
  const [removeInputValue, setRemoveInputValue] = useState("");
  const [removeInputLoading, setRemoveInputLoading] = useState(false);
  const [showRemoveInput, setShowRemoveInput] = useState(false);

  const handleRemoveConfirm = async () => {
    setRemoveInputLoading(true);
    try {
      if (isFamilyOwner && targetMember.family_member_type === "fm") {
        // FO removing a registered targetMember
        await familyService.removeMemberFromFamily(
          targetMember._id || targetMember.id,
          currentFamilyId
        );
      } else {
        // FM removing self
        await exitFamilyAsMember({ action: "exit" });
      }
      setDeleted(true);
      navigate("/profile/family/delete-member/confirm", {
        state: {
          name: targetMember.firstName,
          memberType: targetMember.family_member_type, // Pass 'fo' or 'fm'
          deletedType:
            action === "exit"
              ? "self-exit"
              : isFamilyOwner &&
                targetMember.family_member_type === "fm" &&
                targetMember.registered_member
              ? "registered-remove"
              : targetMember.id === loggedInMember.id
              ? "self-delete"
              : "default",
          action,
          familyName: location.state?.familyName,
          memberId: targetMember.id,
        },
      });
    } catch (err: any) {
      showToast({
        message: getErrorMessage(err, "Failed to remove member."),
        variant: "error",
      });
    } finally {
      setRemoveInputLoading(false);
    }
  };

  // Handler for pet profile delete confirm
  const handlePetDeleteConfirm = async () => {
    setDeleteInputLoading(true);
    try {
      await deleteMember(targetMember._id || targetMember.id);
      setDeleted(true);
      navigate("/profile/family/delete-member/confirm", {
        state: {
          name: targetMember.firstName,
          deletedType:
            action === "exit"
              ? "self-exit"
              : isFamilyOwner &&
                targetMember.family_member_type === "fm" &&
                targetMember.registered_member
              ? "registered-remove"
              : targetMember.id === loggedInMember.id
              ? "self-delete"
              : "default",
          action,
          memberId: targetMember.id,
        },
      });
    } catch (err: any) {
      showToast({
        message: getErrorMessage(err, "Failed to delete."),
        variant: "error",
      });
    } finally {
      setDeleteInputLoading(false);
    }
  };

  const handleOtpSuccess = async (otp: string) => {
    setDeleting(true);
    try {
      if (isFamilyOwner && loggedInMember.id === targetMember.id) {
        if (action === "exit") {
          // FO exit flow
          if (pendingNominee) {
            // Nomination flow
            await exitFamilyAsOwner({
              action: "exit",
              newOwnerId: pendingNominee,
            });
          } else {
            // No nominee (no eligible members)
            await exitFamilyAsOwner({ action: "exit" });
          }
          setDeleted(true);
          navigate("/profile/family/delete-member/confirm", {
            state: {
              name: targetMember.firstName,
              deletedType: "self-exit",
              memberId: targetMember.id,
            },
          });
        } else {
          // FO deleting own account (always call exitFamilyAsOwner for FO)
          await exitFamilyAsOwner({ action: "delete" });
          setDeleted(true);
          navigate("/profile/family/delete-member/confirm", {
            state: {
              name: targetMember.firstName,
              deletedType: "self-delete",
            },
          });
        }
      } else {
        // Not FO/self-delete
        await deleteMember(targetMember._id || targetMember.id);
        setDeleted(true);
        navigate("/profile/family/delete-member/confirm", {
          state: {
            name: targetMember.firstName,
            deletedType:
              targetMember.id === loggedInMember.id ? "self-delete" : "default",
            memberId: targetMember.id,
          },
        }); // go to DeleteConfirm
      }
    } catch (err: any) {
      showToast({
        message: getErrorMessage(err, "Failed to delete."),
        variant: "error",
      });
    } finally {
      setDeleting(false);
      setPendingNominee(null);
    }
  };

  // OTP state for MOtp
  const [otpLoading, setOtpLoading] = React.useState(false);
  const [otpField, setOtpField] = useState<"email" | "mobile">("mobile");
  const [otpTargetValue, setOtpTargetValue] = useState("");
  const [dialCode, setDialCode] = useState("");

  // Nomination confirm: after selecting, show OTP
  const handleNominateFO = async () => {
    if (!selectedNewFO) return;
    // Setup OTP state for logged-in member
    let field: "email" | "mobile";
    let value = "";
    let dial = "";
    if (loggedInMember?.email) {
      field = "email";
      value = loggedInMember.email;
    } else if (loggedInMember?.mobile) {
      field = "mobile";
      value = loggedInMember.mobile;
      dial = loggedInMember.dialCode || "";
    } else {
      showToast({
        message: "No valid contact method found.",
        variant: "error",
      });
      return;
    }
    setOtpField(field);
    setOtpTargetValue(value);
    setDialCode(dial);
    setPendingNominee(selectedNewFO); // Remember nominee for OTP success
    setShowNominateFO(false);
    setOtpLoading(true);
    try {
      await otpService.sendOtp({
        send_to: value,
        dialCode: field === "mobile" ? dial : undefined,
        mode: field === "mobile" ? "sms" : "email",
        type: "member_delete",
      });
      setResendTimer(30);
      setShowOtp(true);
    } catch (err: any) {
      showToast({
        message: getErrorMessage(err, "Failed to send OTP."),
        variant: "error",
      });
    } finally {
      setOtpLoading(false);
    }
  };

  // Owner/registered/not-registered logic here if needed
  // Example: show different text or require OTP only for not registered

  return (
    <div className="profile-page-root pb-[5px]">
      <FormHeader
        module="Family"
        memberInfo={loggedInMember}
        title={
          showNominateFO
            ? nominateStage === "typed"
              ? "Confirm decision to leave"
              : nominateStage === "otp"
              ? "Confirm account deletion"
              : "Family Owner nomination"
            : showOtp &&
              ((isFamilyOwner && loggedInMember.id === targetMember.id) ||
                !targetMember.family_member_type ||
                targetMember.family_member_type === "fm")
            ? "Confirm account deletion"
            : showRemoveInput &&
              !isFamilyOwner &&
              action === "exit" &&
              targetMember.family_member_type === "fm"
            ? `Confirm decision to leave`
            : showRemoveInput &&
              isFamilyOwner &&
              action !== "exit" &&
              targetMember.family_member_type === "fm"
            ? "Confirm removal from family"
            : "Confirm account deletion"
        }
        description={
          showNominateFO
            ? nominateStage === "typed"
              ? `Type "${
                  targetMember?.firstName.toUpperCase() || ""
                }" below to leave your Maatra family.`
              : nominateStage === "otp"
              ? "Enter the 6-digit code sent to confirm your account deletion."
              : "Choose someone from your family that has a Maatra account to take over Family Owner responsibilities."
            : showOtp &&
              ((isFamilyOwner && loggedInMember.id === targetMember.id) ||
                !targetMember.family_member_type ||
                targetMember.family_member_type === "fm")
            ? `Enter the 6-digit code sent to confirm your account deletion.`
            : showRemoveInput &&
              !isFamilyOwner &&
              action === "exit" &&
              targetMember.family_member_type === "fm"
            ? `Type "${
                targetMember?.firstName.toUpperCase() || ""
              }" below to leave this Maatra family.`
            : showRemoveInput &&
              isFamilyOwner &&
              action !== "exit" &&
              targetMember.family_member_type === "fm"
            ? `Please type “${
                targetMember?.firstName.toUpperCase() || ""
              }” to remove them from your Maatra family.`
            : `Type “${
                targetMember?.firstName.toUpperCase() || ""
              }” to delete their account from Maatra.`
        }
        hideLearnMore={
          !showNominateFO ||
          nominateStage === "otp" ||
          nominateStage === "typed"
        }
        learnMoreContent={
          <>
            Before deleting your account, you'll need to nominate a new Family
            Owner. They will take over the responsibilities for managing the
            family group on Maatra. <br />
            <br />
            We'll notify them about their new role.
          </>
        }
      />
      <MSpacer mt={26} />
      {/* REMOVE FROM FAMILY: Typed name confirmation UI for FO removing FM */}
      {showRemoveInput &&
      isFamilyOwner &&
      action !== "exit" &&
      targetMember.family_member_type === "fm" ? (
        <div className="flex flex-col items-center justify-center mt-[34px] w-full pb-[10px]">
          <div className="flex flex-row items-center w-full max-w-md">
            <MInputField
              variant="Outlined"
              size="xl"
              placeholder={`Type ${
                targetMember?.firstName.toUpperCase() || ""
              }`}
              value={removeInputValue}
              onChange={(e) => setRemoveInputValue(e.target.value)}
              className="flex-1"
            />
            <MButton
              variant="icon-only-filled"
              size="xl"
              state={removeInputLoading ? "pressed" : "default"}
              onClick={handleRemoveConfirm}
              disabled={
                removeInputValue.trim() !==
                  (targetMember?.firstName.toUpperCase() || "") ||
                removeInputLoading
              }
              icon={<IconArrowNarrowRight size={24} />}
              style={{ marginLeft: 20 }}
            />
          </div>
        </div>
      ) : null}
      {/* REMOVE FROM FAMILY: Typed name confirmation UI for FM exiting self */}
      {showRemoveInput &&
      !isFamilyOwner &&
      action === "exit" &&
      targetMember.family_member_type === "fm" ? (
        <div className="flex flex-col items-center justify-center mt-[34px] w-full pb-[10px]">
          <div className="flex flex-row items-center w-full max-w-md">
            <MInputField
              variant="Outlined"
              size="xl"
              placeholder={`Type ${
                targetMember?.firstName.toUpperCase() || ""
              }`}
              value={removeInputValue}
              onChange={(e) => setRemoveInputValue(e.target.value)}
              className="flex-1"
            />
            <MButton
              variant="icon-only-filled"
              size="xl"
              state={removeInputLoading ? "pressed" : "default"}
              onClick={handleRemoveConfirm}
              disabled={
                removeInputValue.trim() !==
                  (targetMember?.firstName.toUpperCase() || "") ||
                removeInputLoading
              }
              icon={<IconArrowNarrowRight size={24} />}
              style={{ marginLeft: 20 }}
            />
          </div>
        </div>
      ) : /* PET PROFILE: Type DELETE confirmation UI */
      showDeleteInput &&
        (targetMember?.memberType === "p" || isChild(targetMember)) ? (
        <div className="flex flex-row items-center w-full max-w-md mt-8">
          <MInputField
            variant="Outlined"
            size="xl"
            placeholder={`Type ${targetMember?.firstName.toUpperCase() || ""}`}
            value={deleteInputValue}
            onChange={(e) => setDeleteInputValue(e.target.value)}
            className="flex-1"
          />
          <MButton
            variant="icon-only-filled"
            size="xl"
            state={deleteInputLoading ? "pressed" : "default"}
            onClick={handlePetDeleteConfirm}
            disabled={
              deleteInputValue.trim() !==
                (targetMember?.firstName.toUpperCase() || "") ||
              deleteInputLoading
            }
            icon={<IconArrowNarrowRight size={24} />}
            style={{ marginLeft: 20 }}
          />
        </div>
      ) : showNominateFO ? (
        <div className="flex flex-col justify-center">
          <NominateFamilyOwner
            eligibleOwnership={eligibleForOwnership.map((m) => ({
              id: m.id,
              name: `${m.first_name}`.trim(),
              relationship: m.relationship || "",
              initials: (m.firstName?.[0] || "") + (m.lastName?.[0] || ""),
            }))}
            action={action}
            loggedInMember={loggedInMember}
            onSuccess={() => {
              setShowNominateFO(false);
              navigate("/signup"); // or wherever you want to go after success
            }}
            onStageChange={setNominateStage}
          />
        </div>
      ) : showOtp ? (
        <div className="flex flex-col items-center justify-center w-full max-w-md mx-auto mt-12">
          {/* OTP Info Row (Code sent to ...) */}
          <div className="flex flex-row items-center w-full mb-[24px]">
            <span className="body-base-medium color-neutral-N400">
              Code sent to {otpTargetValue}
            </span>
          </div>
          {/* OTP Input + Arrow Button Row */}
          <div className="flex flex-row items-center w-full gap-4 mb-2">
            <div className="flex-1">
              <MInputField
                size="xl"
                variant="Outlined"
                type="text"
                inputMode="numeric"
                placeholder="Enter 6-digit code"
                value={otpValue}
                autoFocus
                maxLength={6}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setOtpValue(val);
                }}
                className="w-full"
              />
            </div>
            <MButton
              variant="icon-only-filled"
              size="xl"
              onClick={async () => {
                setOtpLoading(true);
                try {
                  await otpService.verifyOtp({
                    send_to: otpTargetValue,
                    dialCode: otpField === "mobile" ? dialCode : undefined,
                    mode: otpField === "mobile" ? "sms" : "email",
                    type: "member_delete",
                    otp: otpValue,
                  });
                  await handleOtpSuccess(otpValue);
                } catch (err: any) {
                  showToast({
                    message: getErrorMessage(err, "OTP verification failed"),
                    variant: "error",
                  });
                } finally {
                  setOtpLoading(false);
                }
              }}
              disabled={otpValue.length !== 6 || otpLoading}
              leftIcon={<IconArrowNarrowRight className="w-8 h-8" />}
              aria-label="Continue"
            />
          </div>
          <div className="w-full flex flex-col gap-1">
            <div className="flex flex-row items-center w-full justify-between mt-[20px]">
              <span className="mb-0" style={{ fontSize: 16, fontWeight: 600 }}>
                Didn't get a code?
              </span>
              <MButton
                variant="link"
                size="md"
                className="ml-auto"
                style={{ position: "relative" }}
                disabled={resendTimer > 0 || otpLoading}
                onClick={async () => {
                  // Track resend OTP click (only for delete, not remove)
                  if (mode === "delete") {
                    trackUserDataLoaded({
                      memberInfo: loggedInMember,
                      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
                      screenName:
                        ANALYTICS_SCREEN_NAMES.FAMILY_INFO_MEMBER_DELETE_CONFIRM_RESEND,
                    });
                  }

                  setOtpLoading(true);
                  try {
                    await otpService.sendOtp({
                      send_to: otpTargetValue,
                      dialCode: otpField === "mobile" ? dialCode : undefined,
                      mode: otpField === "mobile" ? "sms" : "email",
                      type: "member_delete",
                    });
                    setResendTimer(30);
                  } catch (err: any) {
                    showToast({
                      message: getErrorMessage(err, "Failed to resend OTP."),
                      variant: "error",
                    });
                  } finally {
                    setOtpLoading(false);
                  }
                }}
              >
                Resend
              </MButton>
            </div>
            {resendTimer > 0 && (
              <div className="flex flex-row items-center w-full justify-between">
                <span
                  style={{ fontSize: 16, fontWeight: 600, color: "#606060" }}
                >
                  You can request new one in{" "}
                  <span className="font-bold">
                    00:{resendTimer.toString().padStart(2, "0")}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        !showRemoveInput &&
        !showDeleteInput &&
        !showNominateFO &&
        !showOtp && (
          <DeleteAccountDialog
            isOpen={true}
            onClose={() => navigate(-1)}
            onConfirm={async () => {
              await handleConfirm();
            }}
            type={
              action === "delete" &&
              targetMember.family_member_type === "fm" &&
              loggedInMember.id === targetMember.id
                ? "fm-delete"
                : action === "exit" &&
                  targetMember.family_member_type === "fm" &&
                  loggedInMember.id === targetMember.id
                ? "fm-exit"
                : action === "remove"
                ? "remove"
                : action === "exit"
                ? "exit"
                : action === "delete"
                ? "delete"
                : action === "fo-delete-registered"
                ? "fo-delete-registered"
                : "delete-other"
            }
            familyName={location.state?.familyName}
          />
        )
      )}
    </div>
  );
};

export default DeleteProfile;
